import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import type { KeyboardEvent } from 'react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import type { RegulationObjectGroup } from '@/features/bidding/hooks/useBidOfferCascadingDerivedData'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

type GroupsTabGroupsListProps = {
  regulationObjectGroups: RegulationObjectGroup[]
  capacityByActivationGroup: Map<string, number>
  filteredRegulationObjectGroups: RegulationObjectGroup[]
  selectedRegulationObjectCodes: string[]
  draggingRegulationObjectCodes: string[]
  searchQuery: string
  expandedPanels: string[]
  /** Whether there are any groups available for the selected delivery date (before filtering/assignment) */
  hasAnyGroups: boolean
  onRegulationObjectClick: (regulationObjectCode: string, event: React.MouseEvent | React.KeyboardEvent) => void
  onDragStart: (regulationObjectCode: string, event: React.DragEvent) => void
  onDragEnd: () => void
  onAccordionChange: (panelId: string, isExpanded: boolean) => void
  onClearSearch: () => void
}

function GroupsTabGroupsList({
  regulationObjectGroups,
  capacityByActivationGroup,
  filteredRegulationObjectGroups,
  selectedRegulationObjectCodes,
  draggingRegulationObjectCodes,
  searchQuery,
  expandedPanels,
  hasAnyGroups,
  onRegulationObjectClick,
  onDragStart,
  onDragEnd,
  onAccordionChange,
  onClearSearch,
}: Readonly<GroupsTabGroupsListProps>) {
  const { t } = useTranslation()
  const theme = useTheme()
  const listRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = (regulationObjectCode: string, event: KeyboardEvent<HTMLDivElement>, index: number) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        onRegulationObjectClick(regulationObjectCode, event)
        break
      case 'ArrowDown':
        event.preventDefault()
        if (index < filteredRegulationObjectGroups.length - 1) {
          const nextElement = listRef.current?.querySelector(`[data-index="${index + 1}"]`) as HTMLElement | null
          nextElement?.focus()
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (index > 0) {
          const prevElement = listRef.current?.querySelector(`[data-index="${index - 1}"]`) as HTMLElement | null
          prevElement?.focus()
        }
        break
    }
  }

  if (!hasAnyGroups) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
        <CustomTypography color="text.secondary" variant="body2">
          {t('bidding.bidding_cascading.groups_tab.groups_list.empty')}
        </CustomTypography>
        <CustomTypography color="text.secondary" variant="caption">
          {t('bidding.bidding_cascading.groups_tab.groups_list.empty_hint')}
        </CustomTypography>
      </Stack>
    )
  }

  if (filteredRegulationObjectGroups.length === 0 && searchQuery) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <SearchOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <CustomTypography color="text.secondary" variant="body2">
          {t('bidding.bidding_cascading.groups_tab.groups_list.search_no_matches', { searchQuery })}
        </CustomTypography>
        <Button size="small" variant="outlined" onClick={onClearSearch}>
          {t('bidding.bidding_cascading.groups_tab.groups_list.clear_search')}
        </Button>
      </Stack>
    )
  }

  if (regulationObjectGroups.length === 0) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CustomTypography color="text.secondary" variant="body2">
          {t('bidding.bidding_cascading.groups_tab.groups_list.all_assigned')}
        </CustomTypography>
      </Stack>
    )
  }

  const handleAccordionChange = (panelId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    onAccordionChange(panelId, isExpanded)
  }

  return (
    <Box ref={listRef} aria-label={t('bidding.bidding_cascading.groups_tab.groups_list.aria_label')} role="listbox">
      {filteredRegulationObjectGroups.map((regGroup, index) => {
        const isSelected = selectedRegulationObjectCodes.includes(regGroup.regulationObjectCode)
        const isDragging = draggingRegulationObjectCodes.includes(regGroup.regulationObjectCode)

        const classNames = [isSelected && 'is-selected', isDragging && 'is-dragging'].filter(Boolean).join(' ')

        return (
          <Box
            key={regGroup.regulationObjectCode}
            draggable
            aria-selected={isSelected}
            className={classNames}
            data-index={index}
            role="option"
            sx={{
              display: 'flex',
              backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              borderRadius: 1,
              border: '1px solid',
              borderColor: isSelected ? 'primary.main' : 'transparent',
              opacity: isDragging ? 0.5 : 1,
              mb: 0.5,
              transition: 'background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease',
              '@media (hover: hover)': {
                '&:hover': {
                  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.12) : 'action.hover',
                },
              },
              '&:focus-visible': {
                outline: 'none',
                boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
              },
              '&:active': {
                transform: 'scale(0.99)',
              },
            }}
            tabIndex={index === 0 ? 0 : -1}
            onDragEnd={onDragEnd}
            onDragStart={(event) => onDragStart(regGroup.regulationObjectCode, event)}
            onKeyDown={(event) => handleKeyDown(regGroup.regulationObjectCode, event, index)}
          >
            <Box
              aria-hidden="true"
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 0.5,
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
              onClick={(event) => onRegulationObjectClick(regGroup.regulationObjectCode, event)}
            >
              <DragIndicatorIcon
                sx={{
                  fontSize: 18,
                  color: 'text.secondary',
                }}
              />
            </Box>
            <Accordion
              disableGutters
              TransitionProps={{ timeout: 150 }}
              expanded={expandedPanels.includes(regGroup.regulationObjectCode)}
              sx={{
                flex: 1,
                boxShadow: 'none',
                backgroundColor: 'transparent',
                '&:before': { display: 'none' },
              }}
              onChange={handleAccordionChange(regGroup.regulationObjectCode)}
            >
              <AccordionSummary
                aria-label={t('bidding.bidding_cascading.groups_tab.groups_list.expand_aria_label', {
                  code: regGroup.regulationObjectCode,
                })}
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 'auto',
                  px: 1,
                  '& .MuiAccordionSummary-content': {
                    my: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  },
                }}
              >
                <CustomTypography fontWeight="medium" variant="caption">
                  {regGroup.regulationObjectCode}
                </CustomTypography>
                <CustomTypography color="text.secondary" variant="caption">
                  {wattHoursToMegawattHours(regGroup.totalCapacity)} MW
                </CustomTypography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pl: 1 }}>
                {regGroup.activationGroups.map((group) => (
                  <Box
                    key={group.uuid}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                    }}
                  >
                    <CustomTypography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '60%',
                      }}
                      title={group.code}
                      variant="caption"
                    >
                      {group.code}
                    </CustomTypography>
                    <CustomTypography variant="caption">
                      {wattHoursToMegawattHours(capacityByActivationGroup.get(group.uuid) ?? 0)} MW
                    </CustomTypography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          </Box>
        )
      })}
    </Box>
  )
}

export default GroupsTabGroupsList
